import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { compose } from 'redux';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import Modal, {
  MODAL_FOOTER_BUTTON_SIZE,
  MODAL_FOOTER_PRIMARY_BUTTON_MIN_WIDTH,
} from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { useEffectOnce } from '@wix/da-hooks/pkg/useEffectOnce';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import Sidebar from '../_partials/Sidebar';
import UploadButton from '../_partials/UploadButton';
import UploadDropzone from '../_partials/UploadDropzone';
import { MediaSourceType, MediaPopupMode } from '../types';
import { DEFAULT_MEDIASOURCE_TYPES_FOR_MEDIA_SELECTOR } from '../constants';
import MediaSourceMenu from '../_partials/MediaSourceMenu';
import GallectionExplorer from '../Gallections/GallectionExplorer';
import GallectionDeviationSelector from '../Deviations/DeviationSelector/GallectionDeviationSelector';
import BrowseDeviationSelector from '../Deviations/DeviationSelector/BrowseDeviationSelector';
import CommissionSelector from '../Commissions/CommissionSelector';
import PostDeviationSelector from '../Deviations/DeviationSelector/PostDeviationSelector';
import TierSelector from '../Tiers/TierSelector';
import TierExplorer from '../Tiers/TierExplorer';
import { useCurrentMediaSource } from '../_hocs/withCurrentMediaSource';
import { useCurrentGallection } from '../_hocs/withCurrentGallection';
import { useCurrentTier } from '../_hocs/withCurrentTier';
import { useDeviationSelection } from '../_hocs/withSelectedDeviations';
import { useMediaPopupInit } from '../_hocs/withMediaPopupInit';
import { useSearch } from '../_hocs/withSearch';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TierDeviationSelector from '../Deviations/DeviationSelector/TierDeviationSelector';
import DndProviderWrapper from '../../Dnd/DndProviderWrapper';
import { WithFilterProps } from '../_hocs/withFilter';
import withOwnReduxStore from '../_hocs/withOwnReduxStore';

import s from '../MediaPopup.scss';

export type Props = {
  className?: string;
  contentClassName?: string;
  overlayClassName?: string;
  initialMediaSource?: MediaSourceType;
  withMediaSourceSelector?: boolean;
  mediaSourceTypes?: MediaSourceType[];
  selectionLimit?: number;
  allowUploads?: boolean;
  allowMultipleUploads?: boolean;
  title?: string;
  subheading?: React.ReactNode;
  submitBtnLabel?: string;
  gruser?: PapiUser;
  onSelect?: (selectedDeviations: PapiDeviation[]) => void;
  onClose?: () => void;
} & WithFilterProps;

const MediaSelector: React.FC<Props> = ({
  initialMediaSource = MediaSourceType.GALLERY,
  mediaSourceTypes,
  withMediaSourceSelector = true,
  selectionLimit = 1,
  allowedDeviationTypes,
  allowedDeviationResourceTypes,
  allowGifs = true,
  allowPremium = false,
  allowInTierContent = true,
  allowMature = true,
  allowUploads = true,
  allowMultipleUploads = true,
  allowDefaultImage = true,
  ignoreDeviationIds,
  minDeviationWidth,
  minDeviationHeight,
  gruser,
  className,
  contentClassName,
  onSelect,
  onClose,
  submitBtnLabel,
  title,
  subheading,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  // Hooks
  const { initMediaPopup } = useMediaPopupInit();
  const { currentMediaSource } = useCurrentMediaSource();
  const { currentGallection } = useCurrentGallection();
  const { currentTier } = useCurrentTier();
  const { selectedDeviations } = useDeviationSelection();
  const { searchQuery } = useSearch();

  const onSelectHandler = () => onSelect && onSelect(selectedDeviations);

  useEffectOnce(() => {
    initMediaPopup({
      mode: MediaPopupMode.SELECTOR,
      mediaSource: initialMediaSource!,
      mediaSourceTypes:
        mediaSourceTypes ?? DEFAULT_MEDIASOURCE_TYPES_FOR_MEDIA_SELECTOR,
      withMediaSourceSelector,
      selectionLimit,
      allowedDeviationTypes,
      allowedDeviationResourceTypes,
      allowGifs,
      allowPremium,
      allowInTierContent,
      allowDefaultImage,
      allowMature,
      allowUploads,
      allowMultipleUploads,
      ignoreDeviationIds,
      minDeviationWidth,
      minDeviationHeight,
      gruser,
    });
  });

  const getTitle = () => {
    if (title) {
      return title;
    }

    return selectionLimit && selectionLimit > 1
      ? t('mediapopup.selectDeviations')
      : t('mediapopup.selectDeviation');
  };

  const renderSidebar = () => {
    if (!withMediaSourceSelector) {
      return null;
    }
    return (
      <Sidebar>
        {allowUploads && <UploadButton className={s['upload-btn']} />}
        <MediaSourceMenu mediaSourceTypes={mediaSourceTypes!} />
      </Sidebar>
    );
  };

  const renderContentArea = () => {
    switch (currentMediaSource) {
      case MediaSourceType.BROWSE:
        return <BrowseDeviationSelector />;
      case MediaSourceType.STATUS:
      case MediaSourceType.POLL:
      case MediaSourceType.JOURNAL:
      case MediaSourceType.POSTS:
        return <PostDeviationSelector />;
      case MediaSourceType.TIER:
        return <TierSelector />;
      case MediaSourceType.TIER_DEVIATIONS:
      case MediaSourceType.TIER_POSTS:
        if (!currentTier) {
          return <TierExplorer />;
        }
        return <TierDeviationSelector />;
      case MediaSourceType.COMMISSIONS:
        return <CommissionSelector />;
      default:
        if (!currentGallection && !searchQuery) {
          return <GallectionExplorer />;
        }
        return <GallectionDeviationSelector />;
    }
  };

  return (
    <DndProviderWrapper>
      <Modal
        isOpen
        heading={getTitle()}
        subheading={subheading}
        showHeaderBorder
        showFooterBorder
        className={classnames(s['root'], className)}
        contentClassName={classnames(s['content'], contentClassName)}
        onRequestClose={onClose}
        footerRight={[
          <BrandedButton
            key="submit"
            size={MODAL_FOOTER_BUTTON_SIZE}
            style={{ minWidth: MODAL_FOOTER_PRIMARY_BUTTON_MIN_WIDTH }}
            variant="brand-green"
            onClick={onSelectHandler}
            disabled={!selectedDeviations.length}
          >
            {submitBtnLabel || t('mediapopup.select')}
          </BrandedButton>,
        ]}
      >
        {!isMobile && allowUploads && <UploadDropzone />}
        {!isMobile && renderSidebar()}
        {renderContentArea()}
      </Modal>
    </DndProviderWrapper>
  );
};

MediaSelector.displayName = 'MediaSelector';

const enhance = compose(withOwnReduxStore);

export default enhance(MediaSelector) as React.ComponentType<Props>;
